import { observer } from 'mobx-react-lite';
import * as React from 'react';
import loc from 'owa-localize';
import { isEduStudent } from 'owa-bpos-utils';
import type { Calendar } from 'owa-calendar-cache';
import { getPalette } from 'owa-theme';
import { Icon, IIconStyles } from '@fluentui/react/lib/Icon';
import { Label } from '@fluentui/react/lib/Label';
import {
    trueTimeShown,
    trueTimeShownForSchool,
    trueTimeHidden,
} from './ConnectedAccountCalendars.locstring.json';
import { ControlIcons } from 'owa-control-icons';
import { getCalendarColorScheme } from 'owa-calendar-color-resolver';

const styles = require('./ConnectedAccountCalendars.scss');

export interface ConnectedAccountCalendarsProps {
    calendars: Calendar[] | undefined;
    isTrueTimeEnabled: boolean;
    accountProviderType: string;
}

export default observer(function ConnectedAccountCalendars(
    props: ConnectedAccountCalendarsProps
): JSX.Element {
    const { calendars, isTrueTimeEnabled } = props;

    const themeSymbols = getPalette();

    const defaultCalendarNameClass = isTrueTimeEnabled
        ? styles.shownCalendarNameText
        : styles.hiddenCalendarNameText;
    const defaultCalendarTextClass = isTrueTimeEnabled
        ? styles.shownCalendarLabelText
        : styles.hiddenCalendarLabelText;

    // true time is enabled only for the default calendar for connected account (first in the calendar list)
    const trueTimeEnabledCalendar = calendars[0];
    // all other calendars in connected account are true time disabled
    const trueTimeDisabledCalendars = calendars.slice(1);

    const iconStyleProps: IIconStyles =
        isTrueTimeEnabled && trueTimeEnabledCalendar
            ? {
                  root: {
                      color: getCalendarColorScheme(trueTimeEnabledCalendar.id)?.calPrimary,
                      fontSize: '8px',
                  },
              }
            : { root: { color: themeSymbols.neutralTertiary, fontSize: '8px' } };

    return (
        trueTimeEnabledCalendar && (
            <div className={styles.accountCalendarsList}>
                <div className={styles.accountCalendarContainer}>
                    <div className={styles.accountCalendar}>
                        <Icon
                            iconName={ControlIcons.CircleFill}
                            className={styles.accountCalendarIcon}
                            styles={iconStyleProps}
                        />
                        <Label className={defaultCalendarNameClass}>
                            {trueTimeEnabledCalendar?.displayName}
                        </Label>
                    </div>
                    <Label className={defaultCalendarTextClass}>
                        {isTrueTimeEnabled && isEduStudent()
                            ? loc(trueTimeShownForSchool)
                            : isTrueTimeEnabled
                            ? loc(trueTimeShown)
                            : loc(trueTimeHidden)}
                    </Label>
                </div>
                {trueTimeDisabledCalendars?.map(calendar => (
                    <div className={styles.accountCalendarContainer} key={calendar.id.id}>
                        <div className={styles.accountCalendar}>
                            <Icon
                                iconName={ControlIcons.CircleFill}
                                className={styles.accountCalendarIcon}
                                styles={{
                                    root: { color: themeSymbols.neutralTertiary, fontSize: '8px' },
                                }}
                            />
                            <Label className={styles.hiddenCalendarNameText}>
                                {calendar?.displayName}
                            </Label>
                        </div>
                        <Label className={styles.hiddenCalendarLabelText}>
                            {loc(trueTimeHidden)}
                        </Label>
                    </div>
                ))}
            </div>
        )
    );
});
